import { useEffect, useState, useCallback } from "react";
import { api, mesISOLocal } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TrendingDown, Plus, Download, Trash2, ChevronDown, ChevronRight, Pencil, Check, X, Wallet, Truck, Receipt } from "lucide-react";
import KpiCard from "@/components/KpiCard";
import UsageTip from "@/components/UsageTip";
import MoneyInput from "@/components/MoneyInput";

const cop = (v) => `$ ${(Number(v) || 0).toLocaleString("es-CO")}`;

export default function GastosMensualesPage() {
  const [data, setData] = useState(null);
  const [mes, setMes] = useState(() => mesISOLocal());
  const [openManual, setOpenManual] = useState(false);
  const [form, setForm] = useState({ concepto: "", monto: "", notas: "" });
  const [expTransp, setExpTransp] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ concepto: "", monto: "", notas: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/gastos-mensuales", { params: { mes, ultimos_meses: 6 } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar");
    }
  }, [mes]);
  useEffect(() => { load(); }, [load]);

  const guardarManual = async () => {
    if (!form.concepto.trim() || !form.monto) return toast.error("Concepto y monto son obligatorios");
    try {
      await api.post("/gastos-mensuales/manual", {
        concepto: form.concepto.trim(),
        monto: Number(form.monto),
        notas: form.notas || null,
        mes,
      });
      toast.success("Gasto registrado");
      setOpenManual(false);
      setForm({ concepto: "", monto: "", notas: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const eliminar = async (entryId) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    try {
      await api.delete(`/gastos-mensuales/manual/${entryId}`, { params: { mes } });
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const iniciarEdicion = (g) => {
    setEditingId(g.id);
    setEditForm({ concepto: g.concepto || "", monto: String(g.monto || ""), notas: g.notas || "" });
  };

  const cancelarEdicion = () => {
    setEditingId(null);
    setEditForm({ concepto: "", monto: "", notas: "" });
  };

  const guardarEdicion = async (entryId) => {
    if (!editForm.concepto.trim() || !editForm.monto) return toast.error("Concepto y monto son obligatorios");
    try {
      await api.patch(`/gastos-mensuales/manual/${entryId}`, {
        concepto: editForm.concepto.trim(),
        monto: Number(editForm.monto),
        notas: editForm.notas || null,
        mes,
      });
      toast.success("Gasto actualizado");
      cancelarEdicion();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const descargarExcel = async () => {
    try {
      const { data } = await api.get("/gastos-mensuales/export/excel", { params: { mes }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Gastos-${mes}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar"); }
  };

  if (!data) return <div className="text-center text-muted-foreground p-8">Cargando...</div>;

  const m = data.mes_actual;
  const conceptos = m.conceptos || {};
  const prevMonth = (data.historico || [])[1];
  const prevTotal = prevMonth ? Number(prevMonth.total || 0) : 0;
  const currentTotal = Number(m.total || 0);
  const diff = currentTotal - prevTotal;
  const diffPct = prevTotal > 0 ? (diff / prevTotal) * 100 : 0;

  return (
    <div>
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-primary mb-2">Finanzas</div>
          <h1 className="text-4xl font-display font-bold text-foreground flex items-center gap-3">
            <TrendingDown className="w-8 h-8 text-primary" /> Gastos mensuales
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Transporte + gastos manuales + auto-detectados.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Mes en edición</label>
            <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="h-11 w-40" data-testid="gastos-mes-picker" title="Cambia el mes para revisar o editar registros anteriores" />
          </div>
          <Button variant="outline" className="h-11 self-end" onClick={descargarExcel} data-testid="gastos-excel-btn"><Download className="w-4 h-4 mr-2" /> Excel</Button>
          <Button className="h-11 self-end bg-primary hover:bg-primary/90" onClick={() => setOpenManual(true)} data-testid="gastos-agregar-btn">
            <Plus className="w-4 h-4 mr-2" /> Agregar gasto
          </Button>
        </div>
      </header>

      <UsageTip storageKey="gastos-mensuales" steps={[
        "Los <b>costos de transporte</b> de pedidos >$600.000 se registran automáticamente al despachar.",
        "Puedes agregar, <b>editar o eliminar</b> gastos manuales en cualquier mes — solo cambia el selector arriba.",
        "Exporta a Excel para llevarlo a tu contador.",
      ]} />

      {/* Card total */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiCard icon={Wallet} label="Total del mes" value={cop(currentTotal)} tone="primary" destacado testid="gastos-total"
          sub={prevMonth ? <span className={diff >= 0 ? "text-rose-600" : "text-emerald-600"}>{diff >= 0 ? "▲" : "▼"} {cop(Math.abs(diff))} vs mes anterior ({diffPct.toFixed(1)}%)</span> : null} />
        <KpiCard icon={Truck} to="/pedidos?tab=pedidos" label="Transporte pedidos" value={cop(conceptos.transporte_pedidos || 0)} sub={`${(m.transporte_detalle || []).length} pedidos`} />
        <KpiCard icon={Receipt} label="Gastos manuales" value={cop(conceptos.manuales || 0)} sub={`${(m.gastos_manuales || []).length} entradas`} />
      </div>

      {/* Detalle transporte */}
      <div className="bg-card border border-border rounded-xl mb-6">
        <button
          onClick={() => setExpTransp(!expTransp)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
        >
          <div className="flex items-center gap-2">
            {expTransp ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            <span className="text-sm font-semibold">Detalle transporte ({(m.transporte_detalle || []).length} pedidos)</span>
          </div>
          <span className="text-sm font-bold tabular-nums text-primary">{cop(conceptos.transporte_pedidos || 0)}</span>
        </button>
        {expTransp && (
          <div className="border-t border-border">
            <div className="grid grid-cols-12 gap-2 px-6 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground border-b border-border/60">
              <div className="col-span-2">Fecha</div>
              <div className="col-span-2">Pedido</div>
              <div className="col-span-3">Cliente</div>
              <div className="col-span-2">Vendedor</div>
              <div className="col-span-1 text-right">Total</div>
              <div className="col-span-2 text-right">Costo transp.</div>
            </div>
            {(m.transporte_detalle || []).map((row) => (
              <div key={row.pedido_id} className="grid grid-cols-12 gap-2 px-6 py-2 text-xs border-b border-border/40 last:border-0 hover:bg-muted/20" data-testid={`transporte-row-${row.pedido_id}`}>
                <div className="col-span-2 text-muted-foreground">{(row.dispatched_at || "").slice(0, 10)}</div>
                <div className="col-span-2 font-mono text-primary">{row.numero}</div>
                <div className="col-span-3 truncate">{row.cliente_nombre}</div>
                <div className="col-span-2 truncate text-muted-foreground">{row.vendedor_nombre}</div>
                <div className="col-span-1 text-right tabular-nums">{cop(row.total_pedido)}</div>
                <div className="col-span-2 text-right tabular-nums font-semibold">{cop(row.costo_transporte)}</div>
              </div>
            ))}
            {(m.transporte_detalle || []).length === 0 && (
              <div className="px-6 py-8 text-center text-sm text-muted-foreground">Sin transporte registrado este mes.</div>
            )}
          </div>
        )}
      </div>

      {/* Gastos manuales */}
      <div className="bg-card border border-border rounded-xl">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <span className="text-sm font-semibold">Gastos manuales ({(m.gastos_manuales || []).length})</span>
          <span className="text-sm font-bold tabular-nums text-primary">{cop(conceptos.manuales || 0)}</span>
        </div>
        <div>
          {(m.gastos_manuales || []).map((g) => {
            const isEditing = editingId === g.id;
            if (isEditing) {
              return (
                <div key={g.id} className="grid grid-cols-12 gap-2 px-6 py-3 border-b border-border/40 last:border-0 items-center text-sm bg-primary/5" data-testid={`gasto-edit-${g.id}`}>
                  <div className="col-span-2 text-muted-foreground text-xs">{(g.fecha || "").slice(0, 10)}</div>
                  <div className="col-span-4">
                    <Input value={editForm.concepto} onChange={(e) => setEditForm({ ...editForm, concepto: e.target.value })} className="h-9" data-testid={`edit-concepto-${g.id}`} placeholder="Concepto" />
                  </div>
                  <div className="col-span-3">
                    <Input value={editForm.notas} onChange={(e) => setEditForm({ ...editForm, notas: e.target.value })} className="h-9 text-xs" data-testid={`edit-notas-${g.id}`} placeholder="Notas (opcional)" />
                  </div>
                  <div className="col-span-2 text-right">
                    <MoneyInput value={editForm.monto} onValueChange={(v) => setEditForm({ ...editForm, monto: v })} className="h-9 text-right" data-testid={`edit-monto-${g.id}`} />
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    <button onClick={() => guardarEdicion(g.id)} className="p-1.5 rounded-md bg-emerald-500 text-white hover:bg-emerald-600" data-testid={`save-gasto-${g.id}`} title="Guardar">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={cancelarEdicion} className="p-1.5 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-muted" data-testid={`cancel-gasto-${g.id}`} title="Cancelar">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <div key={g.id} className="grid grid-cols-12 gap-2 px-6 py-3 border-b border-border/40 last:border-0 items-center text-sm" data-testid={`gasto-manual-${g.id}`}>
                <div className="col-span-2 text-muted-foreground">{(g.fecha || "").slice(0, 10)}</div>
                <div className="col-span-4 font-medium">{g.concepto}</div>
                <div className="col-span-3 text-xs text-muted-foreground truncate">{g.notas || ""}</div>
                <div className="col-span-2 text-right tabular-nums font-semibold">{cop(g.monto)}</div>
                <div className="col-span-1 text-right flex items-center justify-end gap-0.5">
                  <button onClick={() => iniciarEdicion(g)} className="p-2 text-muted-foreground/70 hover:text-primary" data-testid={`edit-gasto-${g.id}`} title="Editar">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => eliminar(g.id)} className="p-2 text-muted-foreground/70 hover:text-rose-600" data-testid={`del-gasto-${g.id}`} title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
          {(m.gastos_manuales || []).length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-muted-foreground">Aún no hay gastos manuales.</div>
          )}
        </div>
      </div>

      <Dialog open={openManual} onOpenChange={setOpenManual}>
        <DialogContent data-testid="gasto-manual-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Nuevo gasto manual</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div>
              <Label>Concepto*</Label>
              <Input value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })}
                className="h-11 mt-1" placeholder="Ej: Arriendo, servicios, salario Camilo..."
                data-testid="gasto-concepto" />
            </div>
            <div>
              <Label>Monto (COP)*</Label>
              <MoneyInput value={form.monto}
                onValueChange={(v) => setForm({ ...form, monto: v })}
                className="h-11 mt-1" data-testid="gasto-monto" />
            </div>
            <div>
              <Label>Notas</Label>
              <Input value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })}
                className="h-11 mt-1" placeholder="Opcional" data-testid="gasto-notas" />
            </div>
            <Button onClick={guardarManual} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="gasto-save">
              Registrar gasto
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
